import * as _ from "lodash";

import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";

import { isTier12Customer } from "shared/src/stores/SessionStore";
import { Customer, transformCustomer } from "shared/src/types/customer/Customer";
import * as TrackingUtil from "shared/src/utils/TrackingUtil";
import * as CustomerServiceClient from "../clients/CustomerServiceClient";
import { Store } from "shared/src/stores/Store";

// maps from customerId -> data
let failed: {
    [customerId: string]: boolean;
} = {};
let customers: {
    [customerId: string]: Customer | undefined | null;
} = {};
let alreadyLoading: {
    [customerId: string]: boolean;
} = {};

/**
 * Contains data for the CURRENT and already loaded customers.
 */
class CustomerStoreImpl extends Store {
    constructor() {
        super();
        this.listenTo(SessionStore, this.onSessionUpdate);
        this.onSessionUpdate();
    }

    async loadCustomer(customerId: string) {
        if (alreadyLoading[customerId] || customers[customerId] || failed[customerId]) {
            if (customers[customerId]) {
                this.trigger(customers[customerId]);
            }
            // do nothing, as customer is currently loading / loaded / failed to load
            return;
        }
        alreadyLoading[customerId] = true;
        try {
            const response = await CustomerServiceClient.getCustomer(customerId);
            this._handleCustomerData(response);
        } catch (e) {
            this._handleCustomerLoadFailed(e, customerId);
        }
    }

    onSessionUpdate() {
        if (isTier12Customer()) {
            return;
        }
        const newCustomerId: string | undefined | null = SessionStore.getCustomerId();
        if (newCustomerId) {
            this.loadCustomer(newCustomerId);
        }
    }

    getDeliveryStoresForCurrentCustomer(): string[] {
        const customer: Customer | undefined | null = _.get(customers, SessionStore.getCustomerId());
        if (!customer) {
            return [];
        }
        return _.map(customer.deliveryAddresses, (address) => {
            return address.deliveryStore;
        });
    }

    isCurrentCustomerFsdCustomer() {
        const customer: Customer | undefined | null = _.get(customers, SessionStore.getCustomerId());
        return customer ? customer.validForFsd : false;
    }

    isCurrentCustomerFsdCustomerInSelectedStore() {
        if (!this.isCurrentCustomerFsdCustomer()) {
            return false;
        }
        const fsdStores = this.getDeliveryStoresForCurrentCustomer();
        const currStore = SessionStore.getCustomerStore();
        return _.includes(fsdStores, currStore);
    }

    _handleCustomerLoadFailed(e: unknown, customerId: string): void {
        TrackingUtil.trackUiErrorCa("CustomerStore", "Load Customer failed.", e);
        failed[customerId] = true;
        alreadyLoading[customerId] = false;
        this.trigger(null);
    }

    isFailed(customerId?: string | undefined | null) {
        if (customerId) {
            return failed[customerId];
        } else {
            return failed[SessionStore.getCustomerId()];
        }
    }

    isLoading() {
        return (
            SessionStore.pending() || !!(SessionStore.getCustomerId() && alreadyLoading[SessionStore.getCustomerId()])
        );
    }

    _handleCustomerData(response: any): void {
        let customer;
        const customerId: string = response.customerId;
        try {
            customers[customerId] = transformCustomer(response);
            customer = customers[customerId];
            failed[customerId] = false;
            alreadyLoading[customerId] = false;
        } catch (error) {
            this._handleCustomerLoadFailed(error, customerId);
            return;
        }

        // Set Customer info for tracking
        TrackingUtil.setCustomerInformation(customerId, customer);

        if (customer && SessionStore.getCustomerId() === customerId) {
            if (_.isNil(SessionStore.getCardholderNumber())) {
                // just in case the cardholderNumber was not set.
                SessionStore.setSelectedCustomerId(customerId, customer.primaryCardholderNumber);
            }

            const addressId: string | undefined | null = SessionStore.getSelectedDeliveryAddressId();
            if (addressId) {
                const addresses = customer.deliveryAddresses;
                const foundAddress = _.find(addresses, { integrationId: addressId });
                if (foundAddress) {
                    SessionStore.setDeliveryAddress(foundAddress);
                } else {
                    console.warn(
                        "did not find address in store and in customer, even though there was an address id: ",
                        addressId
                    );
                    SessionStore.setDeliveryAddress(null);
                }
            }
        }
        // Trigger customer without order count
        // We do that, because, in case that order service is not available, we still want to trigger the customer data.
        this.trigger(customer);
    }

    getCustomer(): Customer | undefined | null {
        return customers[SessionStore.getCustomerId()];
    }

    getCustomerStoreSelectorSettings() {
        return {
            isFsdCustomer: this.isCurrentCustomerFsdCustomer(),
            deliveryStores: this.getDeliveryStoresForCurrentCustomer()
        };
    }

    // For testing
    resetStore() {
        failed = {};
        customers = {};
        alreadyLoading = {};
    }
}
export default new CustomerStoreImpl();
